import React, { useState, useEffect } from "react";
import MUIDataTable from "mui-datatables";
import Loader from "../../Loader";
import { Typography, Box } from "@mui/material";

const StrategyAssetTable = ({ strategyData }) => {
  const [strategy, setStrategy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (strategyData && strategyData.length > 0) {
      const assetIdGroups = {};

      strategyData?.forEach((strategyItem) => {
        const assets = strategyItem?.assetsVariance || [];
        assets.forEach((asset) => {
          const assetId = asset?.asset?.id;
          if (!assetIdGroups[assetId]) {
            assetIdGroups[assetId] = {
              assetName: asset?.asset?.name,
              quantity: 0,
              currentPortfolio: 0,
              returnPercent: 0,
              currentPrice:
                asset?.asset?.name === "United States Dollar" ? 1 : asset?.price,
            };
          }
          // Quantity should come directly from API field "quantity"
          assetIdGroups[assetId].quantity += asset?.quantity || 0;
          // Market value
          assetIdGroups[assetId].currentPortfolio += asset?.currentPortfolio || 0;
          // Return % should use pnlper if available, otherwise "returns"
          if (typeof asset?.pnlper === "number") {
            assetIdGroups[assetId].returnPercent = asset.pnlper;
          } else if (typeof asset?.returns === "number") {
            assetIdGroups[assetId].returnPercent = asset.returns;
          }
        });
      });

      const sumArray = Object.keys(assetIdGroups).map((assetId) => ({
        "Asset Name": assetIdGroups[assetId].assetName,
        Quantity: assetIdGroups[assetId].quantity || "",
        Price: typeof assetIdGroups[assetId].currentPrice === 'string' 
          ? parseFloat(assetIdGroups[assetId].currentPrice) 
          : assetIdGroups[assetId].currentPrice || "",
        "Market Value": assetIdGroups[assetId].currentPortfolio || "",
        "Return %": assetIdGroups[assetId].returnPercent,
      }));

      setStrategy(sumArray);
      setLoading(false);
    } else {
      setError("Error loading data. Please try again.");
      setLoading(false);

      setStrategy([]);
    }
  }, [strategyData]);

  const columns = [
    {
      name: "Asset Name",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <Typography
              sx={{
                fontSize: '14px',
                color: '#18275A',
                fontWeight: 500,
              }}
            >
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "Quantity",
      options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({
          align: "center",
        }),
        setCellProps: () => ({
          align: "center",
        }),
        customBodyRender: (value) => {
          return (
            <Typography
              sx={{
                fontSize: '14px',
                color: '#18275A',
                textAlign: 'center',
              }}
            >
              {typeof value === "number" ? value.toFixed(0) : "N/A"}
            </Typography>
          );
        },
      },
    },
    {
      name: "Price",
      options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({
          align: "center",
        }),
        setCellProps: () => ({
          align: "center",
        }),
        customBodyRender: (value) => {
          const numValue = typeof value === 'string' ? parseFloat(value) : value;
          return (
            <Typography
              sx={{
                fontSize: '14px',
                color: '#18275A',
                textAlign: 'center',
              }}
            >
              {!isNaN(numValue) ? `$${numValue.toFixed(2)}` : "N/A"}
            </Typography>
          );
        },
      },
    },
    {
      name: "Market Value",
      options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({
          align: "center",
        }),
        setCellProps: () => ({
          align: "center",
        }),
        customBodyRender: (value) => {
          return (
            <Typography
              sx={{
                fontSize: '14px',
                color: '#18275A',
                textAlign: 'center',
              }}
            >
              {typeof value === "number" ? `$${value.toFixed(2)}` : "N/A"}
            </Typography>
          );
        },
      },
    },
    {
      name: "Return",
      options: {
        filter: true,
        sort: true,
        setCellHeaderProps: () => ({
          align: "right",
        }),
        setCellProps: () => ({
          align: "center",
        }),
        customBodyRender: (value) => {
          const num =
            typeof value === "number" ? value : parseFloat(value || 0) || 0;
          const isNegative = num < 0;
          const display = `${Math.abs(num).toFixed(2)}%`;
          const text = isNegative ? `(${display})` : display;
          const color = isNegative ? "#D32F2F" : "#149E3B";
          return (
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color,
                textAlign: "center",
              }}
            >
              {Number.isFinite(num) ? text : "N/A"}
            </Typography>
          );
        },
      },
    },
  ];

  const options = {
    filter: false,
    filterType: "dropdown",
    responsive: "standard",
    selectableRows: "none",
    download: false,
    print: false,
    viewColumns: false,
    search: false,
    pagination: false,
    customTableBodyFooterRender: () => null,
    setTableProps: () => ({
      sx: {
        "& .MuiTableCell-head": {
          backgroundColor: "#FFFFFF",
          color: "#18275A",
          fontSize: "14px",
          fontWeight: 600,
          fontFamily: "SF Pro Display",
          borderBottom: "1px solid #E0E0E0",
          padding: "12px 16px",
        },
        "& .MuiTableCell-body": {
          borderBottom: "1px solid #E0E0E0",
          padding: "16px",
        },
        "& .MuiTableRow-root:hover": {
          backgroundColor: "#F8F9FA",
        },
      },
    }),
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : error ? (
        <p>{error}</p>
      ) : (
        <Box
          sx={{
            width: "100%",
            mt: 2,
            // remove default Paper/card background from MUIDataTable
            "& .MuiPaper-root": {
              backgroundColor: "transparent",
              boxShadow: "none",
              border: "none",
            },
          }}
        >
          <MUIDataTable
            data={strategy}
            options={options}
            columns={columns}
          />
        </Box>
      )}
    </>
  );
};

export default StrategyAssetTable;
